import { DEV_ENTRA_TOKEN_KEY } from './dev-entra-auth.constants';

/**
 * Dispatched when the dev Entra token is written or cleared in this tab, so React
 * can re-read storage (sessionStorage does not trigger `storage` in the same tab).
 */
export const DEV_ENTRA_TOKEN_CHANGED_EVENT = 'advisor-dev-entra-token-changed';

/**
 * Reads the client-credentials access token used for CPA internal routes.
 * Session is preferred; localStorage is a dev mirror so the same token is visible
 * from another tab (e.g. Console vs invitation link opened in a new tab).
 */
export function readDevEntraAccessToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const fromSession = sessionStorage.getItem(DEV_ENTRA_TOKEN_KEY);
    if (fromSession != null && fromSession !== '') {
      return fromSession;
    }
    const fromLocal = localStorage.getItem(DEV_ENTRA_TOKEN_KEY);
    if (fromLocal != null && fromLocal !== '') {
      return fromLocal;
    }
  } catch {
    return null;
  }
  return null;
}

export function writeDevEntraAccessToken(token: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(DEV_ENTRA_TOKEN_KEY, token);
    localStorage.setItem(DEV_ENTRA_TOKEN_KEY, token);
    window.dispatchEvent(new Event(DEV_ENTRA_TOKEN_CHANGED_EVENT));
  } catch {
    // ignore quota / private mode
  }
}

export function clearDevEntraAccessToken(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(DEV_ENTRA_TOKEN_KEY);
    localStorage.removeItem(DEV_ENTRA_TOKEN_KEY);
    window.dispatchEvent(new Event(DEV_ENTRA_TOKEN_CHANGED_EVENT));
  } catch {
    // ignore
  }
}

export function getDevEntraAccessTokenSnapshot(): string {
  return readDevEntraAccessToken() ?? '';
}

export function getDevEntraAccessTokenServerSnapshot(): string {
  return '';
}

export function subscribeDevEntraAccessToken(
  onStoreChange: () => void,
): () => void {
  const bump = () => onStoreChange();
  const onStorage = (e: StorageEvent) => {
    if (e.key === DEV_ENTRA_TOKEN_KEY || e.key === null) {
      onStoreChange();
    }
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', bump);
  document.addEventListener('visibilitychange', bump);
  window.addEventListener(DEV_ENTRA_TOKEN_CHANGED_EVENT, bump);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', bump);
    document.removeEventListener('visibilitychange', bump);
    window.removeEventListener(DEV_ENTRA_TOKEN_CHANGED_EVENT, bump);
  };
}
